import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Container from '../layout/Container';
import Button from '../ui/Button';
import { scrollToSection } from '../../utils/scrollUtils';

const Hero = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scrollOpacity, setScrollOpacity] = useState(1);  useEffect(() => {
    const handleScroll = () => {
      const opacity = Math.max(1 - window.scrollY / 400, 0);
      setScrollOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const opacity = Math.max(1 - window.scrollY / 400, 0);
      setScrollOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas to full width and height
    const handleResize = () => {
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);

    // Particle animation
    const particles: Particle[] = [];
    const particleCount = Math.floor(window.innerWidth / 8); // Increased particle density
    
    // Define the Particle class within the scope where canvas is guaranteed to be non-null
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      private canvasElement: HTMLCanvasElement;
      private context: CanvasRenderingContext2D;
      
      constructor(canvasElement: HTMLCanvasElement, context: CanvasRenderingContext2D) {
        this.canvasElement = canvasElement;
        this.context = context;
        
        this.x = Math.random() * this.canvasElement.width;
        this.y = Math.random() * this.canvasElement.height;
        this.size = Math.random() * 2 + 0.5; // Adjusted size to be more balanced
        this.speedX = Math.random() * 2 - 1;
        this.speedY = Math.random() * 2 - 1;
        this.color = '#4A90E2'; // Brighter blue color
      }
      
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (this.x > this.canvasElement.width) {
          this.x = 0;
        } else if (this.x < 0) {
          this.x = this.canvasElement.width;
        }
        
        if (this.y > this.canvasElement.height) {
          this.y = 0;
        } else if (this.y < 0) {
          this.y = this.canvasElement.height;
        }
      }
      
      draw() {
        this.context.beginPath();
        this.context.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        this.context.fillStyle = this.color;
        this.context.fill();
      }
    }
    
    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle(canvas, ctx));
    }
    
    // Connection lines function
    function connectParticles() {
      if (!ctx || !canvas) return;
      
      const maxDistance = 100; // Max distance for connection
      for (let a = 0; a < particles.length; a++) {
        for (let b = a; b < particles.length; b++) {
          const dx = particles[a].x - particles[b].x;
          const dy = particles[a].y - particles[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < maxDistance) {
            const opacity = 1 - (distance / maxDistance);
            ctx.strokeStyle = `rgba(74, 144, 226, ${opacity * 0.5})`; // Blue with varied opacity
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }
    }
    
    // Animation loop
    function animate() {
      if (!ctx || !canvas) return;
      
      requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      connectParticles();
    }
    
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <HeroSection id="home">
      <BackgroundCanvas ref={canvasRef} />
      
      <HeroContent>
        <Container>
          <AnimatedContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <SubTitle>Hi, I'm</SubTitle>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Title>Christopher Anto</Title>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Description>
                Senior Product Manager | Sports Tech · Hyperlocal · HealthTech | MBA Loughborough
              </Description>
            </motion.div>
            
            <ButtonGroup>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Button 
                  variant="primary" 
                  size="lg" 
                  onClick={() => scrollToSection('contact')}
                >
                  Contact Me
                </Button>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={() => scrollToSection('projects')}
                >
                  View Projects
                </Button>
              </motion.div>
            </ButtonGroup>

          </AnimatedContent>
        </Container>
      </HeroContent>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: scrollOpacity }}
        transition={{ duration: 0.3 }}
      >
        <ScrollDown onClick={() => scrollToSection('about')}>
          <span>Scroll Down</span>
          <svg 
            width="16" 
            height="24" 
            viewBox="0 0 16 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M8 0L8 20M8 20L1 13M8 20L15 13" 
              stroke="currentColor" 
              strokeWidth="2"
            />
          </svg>
        </ScrollDown>
      </motion.div>
    </HeroSection>
  );
};

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const BackgroundCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: 0 ${props => props.theme.spacing.xl};
`;

const AnimatedContent = styled.div`
  max-width: 800px;
  text-align: left;
`;

const SubTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: 500;
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.secondary};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes['6xl']};
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.md};
  background: linear-gradient(to right, #FFFFFF, ${props => props.theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes['4xl']};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes['2xl']};
  }
`;

const Description = styled.p`
  font-size: ${props => props.theme.fontSizes['2xl']};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing.xl};
  max-width: 600px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  
  button {
    min-width: 180px; // Ensure both buttons have same minimum width
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
    
    button {
      width: 100%;
    }
  }
`;

interface ThemedProps {
  theme: {
    fontSizes: { [key: string]: string };
    colors: { [key: string]: string };
    spacing: { [key: string]: string };
    breakpoints: { [key: string]: string };
  }
}

const ScrollDown = styled.div<ThemedProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  transition: color 0.3s ease;
  z-index: 100;
  
  span {
    margin-bottom: ${props => props.theme.spacing.sm};
  }
  
  svg {
    animation: bounce 2s infinite;
  }
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  @media (max-height: 600px) {
    display: none;
  }
`;

export default Hero;
